
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice"
import { HTMLWidget } from "~/types/widgets"
import { useCart } from "~/hooks/useCart"
import Icon from "~/components/ui/Icon"
import Modal from "~/components/ui/Modal"
import { useId } from "react"
import { useEffect, useRef } from "react"
import { FreeShippingPrice } from "~/loaders/freeShippingPrice"

export interface Props {
    /**
     * @title Valor para frete grátis
     */
    freeShippingValue: FreeShippingPrice
    /**
     * @title Layout
     */
    layout?: 'header' | 'cart'
    /**
     * @title Texto do modal
     */
    modalText?: HTMLWidget
}

export default function FreightBar(
    { freeShippingValue, layout = 'header', modalText = '' }: Props,
) {
    const { cart } = useCart()
    const ref = useRef<HTMLDivElement>(null)
    const animationRef = useRef<Animation | null>(null)
    const id = useId()

    useEffect(() => () => animationRef.current?.cancel(), [])

    const total = cart?.value?.totalizers.find((item) => item.id === 'Items')?.value ?? 0
    const discounts = cart?.value?.totalizers?.find((item) => item.id === 'Discounts')?.value || 0

    const missingValue = freeShippingValue - (total + discounts) / 100
    const reachedFreeShipping = missingValue <= 0
    const valuePercentage = reachedFreeShipping
        ? 100
        : ((freeShippingValue - missingValue) / freeShippingValue) * 100

    useEffect(() => {
        animationRef.current?.cancel()
        animationRef.current = ref.current?.animate(
            [
                {
                    opacity: 0,
                    backgroundPosition: '0% 0%',
                    offset: 0,
                },
                {
                    opacity: 1,
                    offset: 0.25,
                },
                {
                    opacity: 1,
                    offset: 0.75,
                },
                {
                    backgroundPosition: '100% 100%',
                    opacity: 0,
                    offset: 1,
                },
            ],
            {
                duration: 2000,
                direction: 'normal',
                fill: 'forwards',
            },
        ) ?? null
    }, [valuePercentage])

    return (
        <>
            <Modal id={id} alt='Fechar modal da barra de frete'>
                <div className='absolute top-[10%] rounded-none border-4 text-sm border-[#595955] bg-white p-8 pr-10'>
                    <label htmlFor={id} className='absolute top-4 right-4'>
                        <Icon id='XMark' size={18} strokeWidth={4} role='img' aria-label='Fechar' />
                    </label>

                    <div
                        dangerouslySetInnerHTML={{ __html: modalText }}
                        className='leading-loose [&_a]:bg-salmon [&_a]:underline [&_a]:text-white'
                    />
                </div>
            </Modal>
            <label
                htmlFor={id}
                className='relative w-full bg-bege-light text-wine h-[35px] block cursor-pointer'
            >
                <div
                    className='h-[35px] bg-bege transition-all duration-[2000ms]'
                    style={{
                        width: `${valuePercentage}%`,
                    }}
                >
                    <div
                        ref={ref}
                        style={{
                            backgroundImage: `url(${'/bg-effect.png'})`,
                            backgroundPosition: 'center',
                            backgroundRepeat: 'repeat-x',
                        }}
                        className='w-full h-full'
                    />
                </div>
                {cart?.value && (
                    <div
                        className={`absolute inset-0 flex items-center text-sm justify-center gap-x-2 text-center ${
                            layout === 'header' ? ' md:text-base' : ''
                        }`}
                    >
                        {reachedFreeShipping
                            ? (
                                <span>
                                    Parabéns, você ganhou{' '}
                                    <span className='uppercase font-black'>Frete Grátis!</span>{' '}
                                    para o Brasil.
                                </span>
                            )
                            : (
                                <span>
                                    Faltam{' '}
                                    <span className='uppercase font-black'>
                                        {formatPrice(missingValue)}
                                    </span>{' '}
                                    para você ganhar{' '}
                                    <span className='uppercase font-black'>Frete Grátis!</span>
                                </span>
                            )}
                    </div>
                )}
            </label>
        </>
    )
}
